import { ColumnDef } from '@tanstack/react-table'
import type { UpdateRunBookItem } from './api'
import { Button } from '@/components/ui/button'
import { FileJson, FileStack, QrCode, ClipboardList, PlayCircle, CheckCircle2, AlertCircle, Pencil, Notebook } from 'lucide-react'
import { buildIconPath } from '@/lib/imagePaths'
import { AnalyticsStatusIndicator } from '@/lib/utils/analytics-status'
import { TranslatableText } from '@/components/ui/TranslatableText'

/** Format minutes as "Xd Xh Xm remaining" (positive) or "Xd Xh Xm overdue" (negative); includes days, hours, minutes */
const formatTimeRemaining = (minutes: number | null | undefined, t: (key: string) => string): string => {
    if (minutes === null || minutes === undefined) return '-'
    const abs = Math.abs(Math.round(minutes))
    const days = Math.floor(abs / (60 * 24))
    const remainingMins = abs % (60 * 24)
    const h = Math.floor(remainingMins / 60)
    const m = remainingMins % 60
    const parts: string[] = []
    if (days > 0) parts.push(`${days}d`)
    if (h > 0) parts.push(`${h}h`)
    if (m > 0 || parts.length === 0) parts.push(`${m}m`)
    const formatted = parts.join(' ')
    return minutes >= 0
        ? `${formatted} ${t('updateRunBook.timeRemaining')}`
        : `${formatted} ${t('updateRunBook.timeOverdue')}`
}

/** Small doughnut chart showing percentage complete (0-100) */
function DoughnutChart({ percentage, size = 32 }: { percentage: number; size?: number }) {
    const r = (size - 4) / 2
    const circumference = 2 * Math.PI * r
    const pct = Math.min(100, Math.max(0, percentage))
    const strokeDashoffset = circumference * (1 - pct / 100)
    return (
        <div className="relative flex-shrink-0" style={{ width: size, height: size }} title={`${Math.round(pct)}% complete`}>
            <svg width={size} height={size} className="rotate-[-90deg]">
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={r}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={3}
                    className="text-muted-foreground/30"
                />
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={r}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={3}
                    strokeDasharray={circumference}
                    strokeDashoffset={strokeDashoffset}
                    strokeLinecap="round"
                    className="text-green-600 transition-all duration-300"
                />
            </svg>
            <span className="absolute inset-0 flex items-center justify-center text-[10px] font-medium text-foreground">
                {Math.round(pct)}%
            </span>
        </div>
    )
}

const getStatusCircle = (analytics: number | null) => (
    <AnalyticsStatusIndicator status={analytics} size="h-4 w-4" />
)

export function createUpdateRunBookColumns(
    t: (key: string) => string,
    formatDate: (date: Date | string) => string,
    onShowMetadata?: (title: string, content: string | null) => void,
    onShowObjectDetails?: (objectID: string, requestID: string) => void,
    onShowQRCode?: (objectID: string) => void,
    onOpenRunbook?: (row: UpdateRunBookItem) => void,
    onViewUpdates?: (row: UpdateRunBookItem) => void
): ColumnDef<UpdateRunBookItem>[] {
    return [
        {
            accessorKey: 'objectID',
            header: t('recoverRequests.objectID'),
            size: 320,
            minSize: 280,
            cell: ({ row }) => {
                const objectID = row.getValue('objectID') as string | null
                const description = row.original.objectDescription
                const requestID = row.original.requestID
                const requestObjectCount = row.original.requestObjectCount || 0
                const hoverText = [objectID, description].filter(Boolean).join(' — ')
                return (
                    <div className="flex flex-col min-w-0" title={hoverText || undefined}>
                        <span className="text-sm font-medium truncate">{objectID || '-'}</span>
                        {description && (
                            <span className="text-xs text-muted-foreground truncate">
                                <TranslatableText text={description} />
                            </span>
                        )}
                    </div>
                )
            },
        },
        {
            id: 'objectIcon',
            header: '',
            size: 48,
            minSize: 48,
            maxSize: 48,
            meta: { compact: true },
            cell: ({ row }) => {
                const iconFilename = row.original.iconFilename
                if (!iconFilename) return null
                return (
                    <div className="flex items-center justify-center">
                        <img src={buildIconPath(iconFilename)} alt="" className="h-8 w-8 object-contain" />
                    </div>
                )
            },
        },
        {
            id: 'qrCode',
            header: '',
            size: 32,
            minSize: 28,
            maxSize: 36,
            cell: ({ row }) => {
                const objectID = row.getValue('objectID') as string | null
                if (!objectID || !onShowQRCode) return <div className="flex items-center justify-center px-0">-</div>
                return (
                    <div className="flex items-center justify-center px-0">
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={(e) => {
                                e.stopPropagation()
                                onShowQRCode(objectID)
                            }}
                            title={t('objects.showQRCode')}
                        >
                            <QrCode className="h-3.5 w-3.5" />
                        </Button>
                    </div>
                )
            },
        },
        {
            id: 'subObjects',
            header: '',
            size: 32,
            minSize: 28,
            maxSize: 36,
            cell: ({ row }) => {
                const objectID = row.getValue('objectID') as string | null
                const requestID = row.original.requestID
                const requestObjectCount = row.original.requestObjectCount || 0
                if (!objectID || !requestID || !onShowObjectDetails || requestObjectCount <= 1) {
                    return <div className="flex items-center justify-center px-0">-</div>
                }
                return (
                    <div className="flex items-center justify-center px-0">
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={(e) => {
                                e.stopPropagation()
                                onShowObjectDetails(objectID, requestID)
                            }}
                            title={t('recoverRequests.viewObjectDetails')}
                        >
                            <FileStack className="h-3.5 w-3.5" />
                        </Button>
                    </div>
                )
            },
        },
        {
            accessorKey: 'versionMetadata',
            header: '',
            size: 32,
            minSize: 28,
            maxSize: 36,
            cell: ({ row }) => {
                const metadata = row.getValue('versionMetadata') as string | null
                if (!metadata) return <div className="flex items-center justify-center px-0"><span className="text-muted-foreground">-</span></div>
                return (
                    <div className="flex items-center justify-center px-0">
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={(e) => {
                                e.stopPropagation()
                                onShowMetadata?.(t('recoverRequests.metadata'), metadata)
                            }}
                        >
                            <FileJson className="h-3.5 w-3.5" />
                        </Button>
                    </div>
                )
            },
        },
        {
            accessorKey: 'versionAnalytics',
            header: t('recoverRequests.analytics'),
            size: 80,
            minSize: 70,
            maxSize: 90,
            cell: ({ row }) => {
                const analytics = row.getValue('versionAnalytics') as number | null
                return (
                    <div className="flex items-center justify-center">
                        {getStatusCircle(analytics)}
                    </div>
                )
            },
        },
        {
            id: 'runbookTasks',
            header: t('updateRunBook.runbookTasks'),
            size: 280,
            minSize: 260,
            maxSize: 350,
            cell: ({ row }) => {
                const noTasks = row.original.noTasks ?? 0
                if (noTasks === 0) {
                    return <span className="text-sm text-muted-foreground">{t('recoverRequests.noRunbook')}</span>
                }
                const noStarted = row.original.noStarted ?? 0
                const noCompleted = row.original.noCompleted ?? 0
                const noIssues = row.original.noIssues ?? 0
                const percentageComplete = row.original.percentageComplete ?? (noCompleted / noTasks) * 100
                const pct = percentageComplete != null ? percentageComplete : 0
                return (
                    <div className="flex items-center gap-2 flex-nowrap" title={`${t('updateRunBook.runbookTaskCount')}: ${noTasks} | ${t('updateRunBook.runbookTaskStarted')}: ${noStarted} | ${t('updateRunBook.runbookTaskCompleted')}: ${noCompleted} | ${t('updateRunBook.runbookTaskIssues')}: ${noIssues}`}>
                        <DoughnutChart percentage={pct} size={36} />
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                            <div className="flex items-center gap-1 rounded px-1.5 py-0.5 bg-primary/10">
                                <ClipboardList className="h-3.5 w-3.5 text-primary" />
                                <span className="text-xs font-medium">{noTasks}</span>
                            </div>
                            <div className="flex items-center gap-1 rounded px-1.5 py-0.5 bg-amber-500/10">
                                <PlayCircle className="h-3.5 w-3.5 text-amber-600" />
                                <span className="text-xs font-medium">{noStarted}</span>
                            </div>
                            <div className="flex items-center gap-1 rounded px-1.5 py-0.5 bg-green-500/10">
                                <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                                <span className="text-xs font-medium">{noCompleted}</span>
                            </div>
                            <div className="flex items-center gap-1 rounded px-1.5 py-0.5 bg-red-500/10">
                                <AlertCircle className="h-3.5 w-3.5 text-red-600" />
                                <span className="text-xs font-medium">{noIssues}</span>
                            </div>
                        </div>
                    </div>
                )
            },
        },
        {
            accessorKey: 'location',
            header: t('recoverRequests.location'),
            size: 160,
            minSize: 130,
            maxSize: 200,
            cell: ({ row }) => {
                const location = row.getValue('location') as string | null
                return <span className="text-sm">{location ? <TranslatableText text={location} /> : '-'}</span>
            },
        },
        {
            accessorKey: 'teamName',
            header: t('recoverRequests.teamName'),
            size: 160,
            minSize: 140,
            maxSize: 220,
            cell: ({ row }) => {
                const teamName = row.getValue('teamName') as string | null
                return <span className="text-sm">{teamName ? <TranslatableText text={teamName} /> : '-'}</span>
            },
        },
        {
            accessorKey: 'forecastFinishTime',
            header: t('recoverRequests.forecastFinishTime'),
            size: 160,
            minSize: 140,
            maxSize: 180,
            cell: ({ row }) => {
                const forecastFinishTime = row.getValue('forecastFinishTime') as string | Date | null
                return (
                    <span className="text-sm whitespace-nowrap">
                        {forecastFinishTime ? formatDate(forecastFinishTime) : '-'}
                    </span>
                )
            },
        },
        {
            accessorKey: 'timeRemaining',
            header: t('updateRunBook.timeRemainingColumn'),
            size: 140,
            minSize: 120,
            maxSize: 180,
            cell: ({ row }) => {
                const timeRemaining = row.original.timeRemaining
                const formatted = formatTimeRemaining(timeRemaining, t)
                if (formatted === '-') return <span className="text-sm text-muted-foreground">-</span>
                const isOverdue = (timeRemaining ?? 0) < 0
                return (
                    <span className={`text-sm whitespace-nowrap font-medium ${isOverdue ? 'text-red-600' : 'text-green-600'}`}>
                        {formatted}
                    </span>
                )
            },
        },
        {
            id: 'viewUpdates',
            header: '',
            size: 44,
            minSize: 36,
            maxSize: 48,
            cell: ({ row }) => {
                if (!onViewUpdates) return null
                const requestID = row.original.id ?? (row.original.requestID != null ? Number(row.original.requestID) : null)
                if (requestID == null || isNaN(requestID)) return null
                return (
                    <div className="flex items-center justify-center px-0">
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
                            onClick={(e) => {
                                e.stopPropagation()
                                onViewUpdates(row.original)
                            }}
                            title={t('updateRunBook.viewUpdates')}
                        >
                            <Notebook className="h-4 w-4" />
                        </Button>
                    </div>
                )
            },
        },
        {
            id: 'edit',
            header: '',
            size: 44,
            minSize: 36,
            maxSize: 48,
            cell: ({ row }) => {
                if (!onOpenRunbook) return <div className="flex items-center justify-center px-0">-</div>
                const requestID = row.original.id ?? (row.original.requestID != null ? Number(row.original.requestID) : null)
                if (requestID == null || isNaN(requestID)) return <div className="flex items-center justify-center px-0">-</div>
                return (
                    <div className="flex items-center justify-center px-0">
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
                            onClick={(e) => {
                                e.stopPropagation()
                                onOpenRunbook(row.original)
                            }}
                            title={t('updateRunBook.openRunbook')}
                        >
                            <Pencil className="h-4 w-4" />
                        </Button>
                    </div>
                )
            },
        },
    ]
}
